import { DialogComponent } from '@theme/dialog';

const OPEN_HASH = '#build-your-box';

/*
 * Build Your Box now lives on its own page. sections/header.liquid loads this
 * script with the page's URL on the tag; the header button is a real link to
 * that page, and this sends every other entry point there too.
 */
const PAGE_URL =
  document.querySelector('script[data-byb-page-url]')?.getAttribute('data-byb-page-url') ||
  '/pages/build-your-box';

/* Placeholder links inside the header and drawer menus only. */
const MENU_LINK = '.menu-drawer a[href="#"], .header a[href="#"], header-drawer a[href="#"]';

/* The navigation menu item's URL is just "#" - there is no page URL on it - so
   it is recognised by its label. Punctuation and spacing are stripped so "Build
   Your Own Box", "BUILD YOUR OWN BOX" and "Build-your-own box" all match. */
const BOX_LABEL = 'buildyourownbox';

/** @param {Element} link */
function isBoxMenuLink(link) {
  return (link.textContent || '').toLowerCase().replace(/[^a-z]/g, '') === BOX_LABEL;
}

/**
 * Build Your Box popup.
 *
 * No longer rendered - the builder moved to a page. Kept defined so that
 * rendering snippets/build-your-box-popup.liquid again brings the popup back
 * unchanged, in which case the triggers below open it instead of navigating.
 *
 * Extends the theme's DialogComponent so scroll locking, click-outside, Escape
 * and the open/close animations behave exactly like the cart drawer.
 */
class BuildYourBoxPopup extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClose);

    // Deep links: /pages/anything#build-your-box opens it on load.
    if (window.location.hash === OPEN_HASH) this.showDialog();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClose);
  }

  /** @param {Event} event */
  #handleClose = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-byb-popup-close]')) this.closeDialog();
  };
}

if (!customElements.get('build-your-box-popup')) {
  customElements.define('build-your-box-popup', BuildYourBoxPopup);
}

/**
 * Sends the customer to the builder: opens the popup if one is on the page,
 * otherwise goes to the Build Your Box page.
 */
function openBuildYourBox() {
  const popup = document.querySelector('build-your-box-popup');
  if (popup instanceof BuildYourBoxPopup) {
    popup.showDialog();
    return;
  }

  // Already on the page (e.g. a #build-your-box link on it): nothing to do.
  if (window.location.pathname === new URL(PAGE_URL, window.location.origin).pathname) return;

  window.location.assign(PAGE_URL);
}

/*
 * Links shared before the move - /anything#build-your-box - used to open the
 * popup on load. Without a popup they would land on a page with nothing to open,
 * so they go to the Build Your Box page instead.
 */
if (window.location.hash === OPEN_HASH && !document.querySelector('build-your-box-popup')) {
  openBuildYourBox();
}

/**
 * One delegated listener rather than binding each trigger: theme blocks render
 * their buttons long after this module runs, and sections re-render on every
 * cart update, so anything bound up front would go stale.
 */
document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const trigger = target.closest?.(
    // `*=` not `$=`: a link set to the homepage plus the hash arrives as
    // https://rettrose.in/#build-your-box, which an ends-with would miss.
    `[data-build-your-box-open], a[href*="${OPEN_HASH}"], ${MENU_LINK}`
  );
  if (!trigger) return;

  // A "#" menu link is only ours if it is the Build Your Own Box item; every
  // other placeholder link in the menus is left alone.
  if (trigger.matches(MENU_LINK) && !isBoxMenuLink(trigger)) return;

  event.preventDefault();
  openBuildYourBox();
});
